import type React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import HistoryEntryStatusSummary from "./HistoryEntryStatusSummary";
import UserAvatar from "./UserAvatar";
import { getEntityStatusPaletteColor } from "./OrderStatusChip";
import type { DisplayHistoryEntry } from "../../utils/entityChangeHistory";
import {
  formatDisplayHistoryFieldLabel,
  formatDisplayHistoryValue,
  getHistoryStatusChange,
} from "../../utils/entityChangeHistory";

type TranslationKeys = keyof typeof translations.en;

export interface EntityChangeHistoryTestIds {
  root: string;
  entry: string;
  summary: string;
  change?: string;
}

interface EntityChangeHistorySectionProps {
  title: string;
  histories: DisplayHistoryEntry[];
  testIds: EntityChangeHistoryTestIds;
  sx?: object;
}

const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const EntityChangeHistorySection: React.FC<EntityChangeHistorySectionProps> = ({
  title,
  histories,
  testIds,
  sx,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (histories.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 4,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        ...sx,
      }}
      data-testid={testIds.root}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 2.5, fontWeight: 700 }}>
        {title}
      </Typography>

      <Box sx={{ position: "relative", pl: { xs: 0, sm: 1 } }}>
        {histories.map((history, historyIndex) => {
          const isLast = historyIndex === histories.length - 1;
          const statusChange = getHistoryStatusChange(history.changes);
          const finalStatus = statusChange?.after ?? statusChange?.before;
          const nodeColor = finalStatus
            ? getEntityStatusPaletteColor(finalStatus, theme)
            : theme.palette.text.disabled;
          const displayName = history.userName || history.userId;
          const historyUser = {
            _id: history.userId,
            name: displayName,
            profilePictureUrl: history.profilePictureUrl,
          };

          return (
            <Box
              key={`${history.timestamp}-${historyIndex}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "28px 1fr", sm: "36px 1fr" },
                gap: { xs: 1, sm: 1.5 },
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: nodeColor,
                    border: "2px solid",
                    borderColor: "background.paper",
                    boxShadow: `0 0 0 2px ${nodeColor}`,
                    mt: 2.25,
                    zIndex: 1,
                    flexShrink: 0,
                  }}
                />
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      width: 2,
                      bgcolor: "divider",
                      my: 0.5,
                      minHeight: 24,
                    }}
                  />
                )}
              </Box>

              <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                data-testid={testIds.entry}
                sx={{
                  mb: isLast ? 0 : 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "12px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": {
                    margin: 0,
                    mb: isLast ? 0 : 1.5,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 0.5,
                    "& .MuiAccordionSummary-content": {
                      my: 1.25,
                      alignItems: "center",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                      pr: 1,
                      minWidth: 0,
                    }}
                  >
                    <UserAvatar user={historyUser} size={28} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("orders.historyUser" as TranslationKeys)}: {displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(history.timestamp)}
                      </Typography>
                    </Box>
                    <HistoryEntryStatusSummary
                      changes={history.changes}
                      data-testid={testIds.summary}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, borderTop: 1, borderColor: "divider" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("orders.historyField" as TranslationKeys)}</TableCell>
                        <TableCell>{t("orders.historyBefore" as TranslationKeys)}</TableCell>
                        <TableCell>{t("orders.historyAfter" as TranslationKeys)}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.changes.map((change, changeIndex) => (
                        <TableRow
                          key={`${change.field}-${changeIndex}`}
                          data-testid={testIds.change}
                        >
                          <TableCell>
                            {formatDisplayHistoryFieldLabel(change.field, (key) =>
                              t(key as TranslationKeys),
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDisplayHistoryValue(change.field, change.before, (key) =>
                              t(key as TranslationKeys),
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDisplayHistoryValue(change.field, change.after, (key) =>
                              t(key as TranslationKeys),
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default EntityChangeHistorySection;
